import React, { useMemo, useState, useEffect, startTransition, ReactElement } from 'react';
import { useSelector } from 'react-redux';
import { MultiSelect } from 'primereact/multiselect';
import Notification from '../../../Common/Notification';
import LoadingIndicator from '../../../Common/LoadingIndicator/LoadingIndicator';
import TaskCard from '../TaskCard/TaskCard';
import LocationTaskSidebar from '../../../TaskList/TaskDetail/LocationTaskSidebar/LocationTaskSidebar';
import { Option } from '../../../../types/common';
import {
    useLoadPrioritizedTasks,
    useLoadColumnFilters,
    useSearchResponsibleUsers,
    useUpdateLocationTaskStatus,
    PrioritizedTasksRequest,
} from '../../../../apis/todaysFocus';
import { DashboardSetting } from '../../../../apis/companyDashboard';
import { determineLocationKeysForFilters } from '../../../../helpers/taskListHelpers';
import { CORE_STATUSES } from '@Constants/constants';
import './TodaysFocus.scss';

const URGENCY_LEVELS = [
    { label: 'Critical', value: 'Critical' },
    { label: 'High', value: 'High' },
    { label: 'Medium', value: 'Medium' },
    { label: 'Low', value: 'Low' },
];

const DEFAULT_URGENCY_LEVELS = ['Critical', 'High', 'Medium'];
const NO_URGENCY_SENTINEL = '__NONE__';

// id-array dashboard filter fields shared between the task and location attribute groups.
const DASHBOARD_FILTER_ID_FIELDS = [
    'disciplineIds',
    'departmentIds',
    'phaseIds',
    'communityIds',
    'baselineIds',
    'brandIds',
    'areaIds',
    'regionIds',
    'managementCompanyIds',
    'locationTypeIds',
    'continentIds',
    'locationIds',
    'tagIds',
] as const;

// Pure helper (exported for unit testing): maps a DashboardSetting to the subset of
// PrioritizedTasksRequest filter fields, omitting empty arrays and false isMilestonesOnly.
export const toTaskFilterParams = (settings?: DashboardSetting): Partial<PrioritizedTasksRequest> => {
    if (!settings) return {};
    const params: Partial<PrioritizedTasksRequest> = {};
    for (const field of DASHBOARD_FILTER_ID_FIELDS) {
        const ids = settings[field];
        if (Array.isArray(ids) && ids.length > 0) {
            (params as Record<string, number[]>)[field] = ids;
        }
    }
    if (settings.isMilestonesOnly) {
        params.isMilestonesOnly = true;
    }
    return params;
};

interface TodaysFocusProps {
    selectedProjectIds?: number[];
    dashboardSettings?: DashboardSetting;
}

const TodaysFocus = ({ selectedProjectIds = [], dashboardSettings }: TodaysFocusProps): ReactElement => {
    // Task drawer state
    const [isTaskDrawerVisible, setIsTaskDrawerVisible] = useState<boolean>(false);
    const [selectedTaskId, setSelectedTaskId] = useState<number>(0);
    const [selectedLocationKey, setSelectedLocationKey] = useState<string>('');
    const [selectedUrgencyLevels, setSelectedUrgencyLevels] = useState<string[]>(DEFAULT_URGENCY_LEVELS);

    const subscriptionId: string = useSelector((state: any) =>
        state.user.currentSubscriptionId ? state.user.currentSubscriptionId.toString() : ''
    );

    // ---- Build stable query request objects ----

    const tasksRequest = useMemo<PrioritizedTasksRequest>(() => {
        const effectiveProjectIds = dashboardSettings?.projectIds?.length
            ? dashboardSettings.projectIds
            : selectedProjectIds;
        return {
            subscriptionId: subscriptionId ? parseInt(subscriptionId, 10) : undefined,
            urgencyLevels: selectedUrgencyLevels.length > 0 ? selectedUrgencyLevels : [NO_URGENCY_SENTINEL],
            maxDaysUntilDue: 90,
            ...(effectiveProjectIds.length > 0 ? { projectIds: effectiveProjectIds } : {}),
            ...toTaskFilterParams(dashboardSettings),
        };
    }, [subscriptionId, selectedUrgencyLevels, selectedProjectIds, dashboardSettings]);

    const { data: tasksData, refetch: refetchTasks, isFetching: isFetchingTasks = false } = useLoadPrioritizedTasks(tasksRequest);
    const tasks = tasksData.tasks;

    const locationKeys: string[] = useMemo(
        () => Array.from(new Set(tasks.map(task => task.locationKey).filter(Boolean))),
        [tasks]
    );

    const responsibleUsersRequest = useMemo(() => ({
        locationKeys,
        searchTerm: '',
    }), [locationKeys]);

    const columnFiltersRequest = useMemo(() => ({
        subscriptionId,
        locationKeys,
    }), [subscriptionId, locationKeys]);

    // ---- TanStack Query hooks (useSuspenseQuery — parent must wrap with Suspense) ----

    const { data: columnFiltersData } = useLoadColumnFilters(columnFiltersRequest);
    const { data: responsibleUsersData } = useSearchResponsibleUsers(responsibleUsersRequest);

    // ---- Derived data ----

    const priorityThreshold = tasksData.priorityThreshold ?? 50;
    const generatedAt = tasksData.generatedAt ?? '';



    const statuses: Option[] = useMemo(
        () => columnFiltersData.statuses.map(s => ({
            id: s.id,
            name: s.name,
            key: s.id,
            value: s.id.toString(),
            coreStatusId: s.coreStatusId ?? undefined,
        })),
        [columnFiltersData.statuses]
    );

    const responsibleUsers: Option[] = useMemo(
        () => responsibleUsersData.users.map(u => ({
            id: u.id,
            name: u.name,
            key: u.id,
            value: u.id.toString(),
        })),
        [responsibleUsersData.users]
    );

    // ---- Status update mutation ----

    const updateStatusMutation = useUpdateLocationTaskStatus(() => {
        refetchTasks();
    });

    // taskId -> optimistically-selected statusId, until the server list reflects the change
    const [statusOverrides, setStatusOverrides] = useState<Record<number, number>>({});

    // ---- Handlers ----

    const handleTaskClick = (taskId: number) => {
        const task = tasks.find(t => t.taskId === taskId);
        if (task) {
            setSelectedTaskId(taskId);
            setSelectedLocationKey(task.locationKey ?? '');
            setIsTaskDrawerVisible(true);
        }
    };

    const handleCloseDrawer = () => {
        setIsTaskDrawerVisible(false);
        setSelectedTaskId(0);
        setSelectedLocationKey('');
    };

    const handleDrawerComplete = () => {
        refetchTasks();
        handleCloseDrawer();
    };

    const handleStatusChange = async (taskId: number, statusId: number): Promise<void> => {
        setStatusOverrides(prev => ({ ...prev, [taskId]: statusId }));
        updateStatusMutation.mutate(
            { locationTaskIds: [taskId], statusId },
            {
                onError: () => {
                    setStatusOverrides(prev => {
                        const next = { ...prev };
                        delete next[taskId];
                        return next;
                    });
                    Notification('error', 'Error', 'Failed to update task status');
                },
            }
        );
    };

    // ---- Optimistic status overlay ----

    // Drop an override once the refetched list catches up to it (or no longer has the task)
    useEffect(() => {
        setStatusOverrides(prev => {
            const ids = Object.keys(prev);
            if (ids.length === 0) return prev;
            let changed = false;
            const next: Record<number, number> = {};
            for (const idStr of ids) {
                const id = Number(idStr);
                const serverTask = tasks.find(t => t.taskId === id);
                if (serverTask && serverTask.statusId !== prev[id]) {
                    next[id] = prev[id];
                } else {
                    changed = true;
                }
            }
            return changed ? next : prev;
        });
    }, [tasks]);

    const coreStatusByStatusId = useMemo(() => {
        const map = new Map<number, number | undefined>();
        statuses.forEach(s => map.set(s.id, s.coreStatusId));
        return map;
    }, [statuses]);

    // Apply optimistic overrides, then hide completed tasks — they no longer need attention
    const displayTasks = useMemo(() => {
        if (selectedUrgencyLevels.length === 0) {
            return [];
        }

        return tasks
            .map(task => {
                const override = statusOverrides[task.taskId];
                return override !== undefined ? { ...task, statusId: override } : task;
            })
            .filter(task => {
                const core = task.statusId !== undefined ? coreStatusByStatusId.get(task.statusId) : undefined;
                return core !== CORE_STATUSES.Complete;
            });
    }, [tasks, statusOverrides, coreStatusByStatusId, selectedUrgencyLevels]);

    // ---- Filtered task lists ----

    const highPriorityTasks = displayTasks.filter(task =>
        ['Critical', 'High'].includes(task.urgencyLevel) || task.finalScore >= priorityThreshold
    );
    const lowerPriorityTasks = displayTasks.filter(task =>
        !['Critical', 'High'].includes(task.urgencyLevel) && task.finalScore < priorityThreshold
    );

    // Heading reflects all selected urgency levels (in canonical order); falls back to the
    // generic label when nothing is selected
    const orderedSelected = ['Critical', 'High', 'Medium', 'Low'].filter(level => selectedUrgencyLevels.includes(level));
    const highPriorityLabel = orderedSelected.length > 0 ? orderedSelected.join(', ') : 'High Priority';

    return (
        <>
            <div className="todays-focus">
                <div className="todays-focus__header">
                    <div className="todays-focus__title">
                        <h1>
                            <i className="fa-regular fa-sparkles todays-focus__title-icon" />
                            Today's Focus
                        </h1>
                        <p className="todays-focus__subtitle">
                            {displayTasks.length} task{displayTasks.length !== 1 ? 's' : ''} requiring your attention
                        </p>
                    </div>
                    <div className="todays-focus__filters">
                        <MultiSelect
                            value={selectedUrgencyLevels}
                            options={URGENCY_LEVELS}
                            onChange={e => startTransition(() => setSelectedUrgencyLevels(e.value ?? []))}
                            placeholder="Filter by urgency"
                            display="chip"
                            style={{ minWidth: '200px' }}
                        />
                        <span className="todays-focus__filter-caption">
                            Refetches tasks matching this urgency &mdash; separate from the priority sections below
                        </span>
                    </div>
                    {generatedAt && (
                        <div className="todays-focus__meta">
                            <span className="todays-focus__generated">
                                Updated {new Date(generatedAt).toLocaleTimeString()}
                            </span>
                        </div>
                    )}
                </div>

                <LoadingIndicator isLoading={isFetchingTasks} hideWhileLoading={false} isFullscreen={false}>
                    {displayTasks.length === 0 && (
                        <div className="todays-focus__empty">
                            <div className="empty-state">
                                <i className="fa-regular fa-calendar-check empty-state__icon"></i>
                                <h3>You're all caught up!</h3>
                                <p>No high-priority tasks requiring your immediate attention.</p>
                                {locationKeys.length === 0 && (
                                    <p className="empty-state__hint">
                                        <i className="fa-solid fa-info-circle"></i>
                                        Select locations from your task list to see relevant tasks.
                                    </p>
                                )}
                            </div>
                        </div>
                    )}

                    {highPriorityTasks.length > 0 && (
                        <div className="todays-focus__section">
                            <div className="section-header">
                                <h2 className="section-header__title">
                                    <i className="fa-solid fa-star section-header__icon"></i>
                                    {highPriorityLabel}
                                </h2>
                                <span className="section-header__count">
                                    {highPriorityTasks.length} task{highPriorityTasks.length !== 1 ? 's' : ''}
                                </span>
                            </div>
                            <div className="task-grid">
                                {highPriorityTasks.map(task => (
                                    <TaskCard
                                        key={task.taskId}
                                        task={task}
                                        statuses={statuses}
                                        onTaskClick={handleTaskClick}
                                        onStatusChange={handleStatusChange}
                                        showAvatar={false}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {lowerPriorityTasks.length > 0 && (
                        <div className="todays-focus__section">
                            <div className="section-header">
                                <h2 className="section-header__title">
                                    <i className="fa-regular fa-list section-header__icon"></i>
                                    Needs Your Response
                                </h2>
                                <span className="section-header__count">
                                    {lowerPriorityTasks.length} task{lowerPriorityTasks.length !== 1 ? 's' : ''}
                                </span>
                            </div>
                            <div className="task-grid">
                                {lowerPriorityTasks.map(task => (
                                    <TaskCard
                                        key={task.taskId}
                                        task={task}
                                        statuses={statuses}
                                        onTaskClick={handleTaskClick}
                                        onStatusChange={handleStatusChange}
                                        showAvatar={false}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </LoadingIndicator>
            </div>

            {isTaskDrawerVisible && selectedTaskId > 0 && (
                <LocationTaskSidebar
                    isDrawerVisible={isTaskDrawerVisible}
                    closeDrawer={handleCloseDrawer}
                    changeTask={handleTaskClick}
                    onComplete={handleDrawerComplete}
                    locationTaskId={selectedTaskId}
                    locationKey={selectedLocationKey}
                    responsibleUsers={responsibleUsers}
                    isMobile={window.innerWidth <= 768}
                />
            )}
        </>
    );
};

export default TodaysFocus;
